import { mapApiItemsToCards } from "./contentCardMapper";
import type { PortalContentKind } from "../../../@types/portalAluno";
import type {
  ContentDetailsApiItem,
  ContentDetailsData,
  ContentDetailsRequestConfig,
  ContentProgress,
  ContentProgressItem,
  ContentProgressItemType,
  ContentProgressSection,
  ContentReview,
} from "../../../@types/portalAluno";
import {
  asArray as asRawArray,
  asBoolean,
  asId,
  asNumber,
  asObject as asRawObject,
  asText,
  firstPositiveNumber,
  firstText,
} from "../coerce";
import { firstDurationLabel } from "../duration";

const asObject = (value: unknown) => asRawObject<ContentDetailsApiItem>(value);

const asArray = (value: unknown) => asRawArray<ContentDetailsApiItem>(value);

const decodeHtml = (value: string) =>
  value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

const asParagraphs = (value: unknown) => {
  const text = asText(value);
  if (!text) return [];

  return decodeHtml(
    text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li)>/gi, "\n")
      .replace(/<[^>]*>/g, "")
  )
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const formatDate = (value: unknown) => {
  const text = asText(value);
  if (!text) return undefined;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return undefined;

  return new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(date);
};

const getExtension = (value?: string) => {
  if (!value) return "";
  const path = value.split(/[?#]/)[0].replace(/\/+$/, "");
  const fileName = path.split("/").pop() ?? "";
  const dotIndex = fileName.lastIndexOf(".");

  return dotIndex >= 0 ? fileName.slice(dotIndex).toLocaleLowerCase() : "";
};

const VIDEO_EXTENSIONS = [
  ".mp4",
  ".mov",
  ".m4v",
  ".avi",
  ".mkv",
  ".3gp",
  ".3gpp",
  ".m3u8",
  ".webm",
];
const AUDIO_EXTENSIONS = [".mp3", ".m4a", ".aac", ".wav", ".ogg", ".oga", ".flac"];
const IMAGE_EXTENSIONS = [".avif", ".gif", ".jpeg", ".jpg", ".png", ".svg", ".webp"];
const DOCUMENT_EXTENSIONS = [".doc", ".docx", ".txt"];
const SPREADSHEET_EXTENSIONS = [".csv", ".xls", ".xlsx"];
const PRESENTATION_EXTENSIONS = [".ppt", ".pptx"];
const HTML_EXTENSIONS = [".htm", ".html"];

const getContentItemType = (
  path?: string,
  contentTypeName?: string,
  isExternal = false
): ContentProgressItemType => {
  const extension = getExtension(path);
  const normalizedType = contentTypeName?.toLocaleLowerCase("pt-BR") ?? "";
  const normalizedPath = path?.toLocaleLowerCase("pt-BR") ?? "";

  if (isExternal) return "external";

  if (normalizedType.includes("scorm") || normalizedPath.includes("/scorm/")) {
    return "scorm";
  }

  if (VIDEO_EXTENSIONS.includes(extension)) {
    return "video";
  }
  if (AUDIO_EXTENSIONS.includes(extension)) {
    return "audio";
  }
  if (extension === ".pdf") {
    return "pdf";
  }
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return "image";
  }
  if (DOCUMENT_EXTENSIONS.includes(extension)) {
    return "document";
  }
  if (SPREADSHEET_EXTENSIONS.includes(extension)) {
    return "spreadsheet";
  }
  if (PRESENTATION_EXTENSIONS.includes(extension)) {
    return "presentation";
  }
  if (HTML_EXTENSIONS.includes(extension)) {
    return "html";
  }
  if (extension === ".zip") {
    return "package";
  }
  if (normalizedType.includes("vídeo") || normalizedType.includes("video")) {
    return "video";
  }
  if (
    normalizedType.includes("áudio") ||
    normalizedType.includes("audio") ||
    normalizedType.includes("podcast")
  ) {
    return "audio";
  }
  if (normalizedType.includes("pdf")) {
    return "pdf";
  }
  if (normalizedType.includes("imagem")) {
    return "image";
  }
  if (
    normalizedType.includes("planilha") ||
    normalizedType.includes("excel") ||
    normalizedType.includes("spreadsheet")
  ) {
    return "spreadsheet";
  }
  if (
    normalizedType.includes("apresentação") ||
    normalizedType.includes("apresentacao") ||
    normalizedType.includes("powerpoint")
  ) {
    return "presentation";
  }
  if (
    normalizedType.includes("documento") ||
    normalizedType.includes("document") ||
    normalizedType.includes("texto")
  ) {
    return "document";
  }
  if (normalizedType.includes("html") || normalizedType.includes("web")) {
    return "html";
  }
  if (normalizedType.includes("pacote") || normalizedType.includes("zip")) {
    return "package";
  }

  // Links internos legados nem sempre chegam com tipo ou extensão. Assim como
  // no app V2, eles continuam abrindo no visualizador web interno.
  return "internal";
};

const getFileName = (path: string | undefined, title: string) => {
  if (!path) return `${title}.pdf`;
  const fileName = path.split(/[?#]/)[0].split("/").pop();
  return fileName || `${title}.pdf`;
};

const formatDuration = (value: unknown) => {
  const text = asText(value);
  if (!text || text.includes("/")) return "";
  return /(?:min|h|:|seg|página)/i.test(text) ? text : `${text} min`;
};

export const mapContentItem = (
  value: unknown,
  idPrefix = "conteudo"
): ContentProgressItem | undefined => {
  const item = asObject(value);
  if (!item) return undefined;
  const entityId = asId(item.idConteudo);
  const title = asText(item.nmConteudo);
  if (!entityId || !title) return undefined;

  const isMarkedExternal = asBoolean(item.isExterno);
  const path = isMarkedExternal
    ? firstText(item.urlExternaConteudo, item.pathConteudo)
    : firstText(item.pathConteudo, item.urlExternaConteudo);
  const detectedContentType = getContentItemType(
    path,
    asText(asObject(item.conteudoTipo)?.nmConteudoTipo) ??
      asText(item.nmConteudoTipo),
    isMarkedExternal
  );
  const opensOutsideApp =
    detectedContentType === "external" || Boolean(path?.includes("//app-conectado"));
  const contentType = opensOutsideApp ? "external" : detectedContentType;

  return {
    completed: asBoolean(item.isRealizado),
    contentType,
    duration: formatDuration(item.dsTempoEstimado),
    entityId,
    externalUrl: opensOutsideApp
      ? firstText(item.urlExternaConteudo, item.pathConteudo)
      : undefined,
    id: `${idPrefix}-${entityId}`,
    imageUrl: asText(item.imgConteudo),
    pdf:
      contentType === "pdf"
        ? {
            fileName: getFileName(path, title),
            sourceUrl: path,
            totalPages: Math.max(1, asNumber(item.totalPages) ?? 1),
          }
        : undefined,
    sourceUrl: !opensOutsideApp ? path : undefined,
    title,
  };
};

const buildSection = (
  id: string,
  title: string,
  kind: ContentProgressSection["kind"],
  items: ContentProgressItem[]
): ContentProgressSection => ({
  completedItems: items.filter((item) => item.completed).length,
  id,
  items,
  kind,
  title,
  totalItems: items.length,
});

const mapEvaluationItems = (value: unknown): ContentProgressItem[] =>
  asArray(value).flatMap((evaluation) => {
    const entityId = asId(evaluation.idAvaliacao);
    const title = asText(evaluation.nmAvaliacao);
    if (!entityId || !title) return [];

    return [
      {
        completed: asBoolean(evaluation.isRealizado),
        contentType: "evaluation" as const,
        duration: "",
        entityId,
        id: `avaliacao-${entityId}`,
        imageUrl: firstText(evaluation.urlImg, evaluation.imgAvaliacao),
        title,
      },
    ];
  });

const mapProgress = (
  raw: ContentDetailsApiItem,
  id: number,
  title: string,
  config: ContentDetailsRequestConfig
): ContentProgress => {
  const sections: ContentProgressSection[] = [];

  if (config.entityType === "trilha") {
    asArray(raw[config.childKey ?? ""]).forEach((course, index) => {
      const courseId = asId(course.idCurso) ?? index + 1;
      const courseTitle = asText(course.nmCurso) ?? `Curso ${index + 1}`;
      const items = asArray(course.cursoConteudos)
        .map((item) => mapContentItem(item, `curso-${courseId}`))
        .filter((item): item is ContentProgressItem => Boolean(item));
      sections.push(
        buildSection(`curso-${courseId}`, courseTitle, "course", items)
      );
    });
  } else {
    const items = asArray(raw[config.childKey ?? ""])
      .map((item) => mapContentItem(item))
      .filter((item): item is ContentProgressItem => Boolean(item));
    if (items.length > 0 || config.entityType !== "conteudo") {
      sections.push(buildSection("conteudos", config.listTitle, "content", items));
    }
  }

  const evaluations = mapEvaluationItems(raw[config.evaluationKey ?? ""]);
  if (evaluations.length > 0) {
    sections.push(buildSection("avaliacoes", "Avaliações", "evaluation", evaluations));
  }

  const contentSections = sections.filter((section) => section.kind !== "evaluation");

  return {
    basis: config.progressBasis,
    completedItems: contentSections.reduce(
      (total, section) => total + section.completedItems,
      0
    ),
    contentId: id,
    listTitle: config.listTitle,
    sections,
    title,
    totalItems: contentSections.reduce(
      (total, section) => total + section.totalItems,
      0
    ),
  };
};

const asPositiveInteger = (value: unknown) => {
  const number = asNumber(value);
  return number !== undefined && Number.isInteger(number) && number > 0
    ? number
    : undefined;
};

const firstPositiveInteger = (...values: unknown[]) => {
  for (const value of values) {
    const number = asPositiveInteger(value);
    if (number !== undefined) return number;
  }

  return undefined;
};

const mapReviews = (raw: ContentDetailsApiItem): ContentReview[] => {
  const possibleReviews =
    raw.feedbacks ?? raw.reviews ?? raw.avaliacoesUsuarios ?? raw.comentarios;

  return asArray(possibleReviews).flatMap((review, index) => {
    const rating = firstPositiveNumber(review.nota, review.rating, review.avaliacao);
    if (!rating) return [];

    return [
      {
        author: firstText(review.nmUsuario, review.autor, review.author) ?? "Usuário",
        authorPhotoUrl: firstText(
          review.imgUsuario,
          review.fotoUsuario,
          review.authorPhotoUrl
        ),
        comment: firstText(review.descricao, review.comentario, review.comment) ?? "",
        id: asId(review.id) ?? asId(review.idFeedback) ?? index + 1,
        rating,
        relativeDate: (() => {
          const date = firstText(review.data, review.relativeDate);
          return date ? formatDate(date) ?? date : "";
        })(),
        showStars: true,
      },
    ];
  });
};

export const mapContentDetailsResponse = (
  value: unknown,
  sourceType: PortalContentKind,
  config: ContentDetailsRequestConfig
): ContentDetailsData => {
  const raw = asObject(value);
  if (!raw) throw new Error("A API retornou detalhes inválidos.");

  const id = asId(raw[config.idKey]);
  const title = asText(raw[config.titleKey]);
  if (!id || !title) {
    throw new Error("O conteúdo solicitado não foi encontrado.");
  }

  const publishedAtIso = asText(raw[config.dateKey]);
  const reviews = mapReviews(raw);
  const primaryContent =
    config.entityType === "conteudo" ? mapContentItem(raw, "principal") : undefined;
  const progress = mapProgress(raw, id, title, config);

  return {
    aboutTitle: config.aboutTitle,
    actionType: config.actionType,
    description: asParagraphs(raw[config.descriptionKey]),
    duration: firstDurationLabel(
      raw.tempoTotal,
      raw.dsTempoEstimado,
      raw.duration
    ),
    entityType: config.entityType,
    hasFeedback: asBoolean(raw.hasFeedback),
    id,
    imageAlt: `Capa de ${title}`,
    imageUrl: asText(raw[config.imageKey]),
    isCompleted: asBoolean(raw.isRealizado),
    isLiked: asBoolean(raw.isLike),
    isSaved: asBoolean(raw.isFavorito),
    moduleRoute: config.moduleRoute,
    moduleTitle: config.moduleTitle,
    modules:
      config.entityType === "conteudo"
        ? undefined
        : firstPositiveInteger(
            raw.totalModulos,
            raw.qtdModulos,
            raw.quantidadeModulos,
            raw.qtdConteudos,
            raw.modules
          ) ?? (progress.totalItems > 0 ? progress.totalItems : undefined),
    primaryContent,
    progress,
    publishedAt: formatDate(publishedAtIso),
    publishedAtIso,
    rating: firstPositiveNumber(
      raw.rating,
      raw.mediaAvaliacao,
      raw.avaliacaoMedia,
      raw.nota
    ),
    relatedCards:
      config.entityType === "conteudo"
        ? mapApiItemsToCards(raw.conteudosRelacionados, "conteudos")
        : [],
    reviews,
    reviewsCount:
      asNumber(raw.reviewsCount) ?? asNumber(raw.totalFeedbacks) ?? reviews.length,
    shareUrl: asText(raw[config.shareKey]),
    sourceType,
    title,
    userComment: asText(raw.comentarioUsuario),
    userRating: firstPositiveNumber(raw.notaUsuario),
  };
};
